/**
 * Settings：AI 分析弹窗的“设置表单”视图层。
 *
 * 设计约束：
 * - 本组件保持纯视图：不直接发起预览/保存请求，只通过 props 回调把用户意图抛给上层 state hook；
 * - 对外只暴露“Preview / Confirm / Cancel”三类动作，避免视图层掺入会话编排。
 *
 * 注意：
 * - 分析范围通过 CascadeFilterComponent 受控渲染，用户选择仅更新草稿态；
 * - 持久化时机由上层 Confirm / Apply 统一控制。
 */
import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import {
  Button,
  Input,
  Select,
} from 'tea-component';
import { useIntl } from 'umi';
import type { SelectOption } from 'tea-component';
import DatalabGrid from '@/pages/datalab/components/DatalabGrid';
import { ALL_VALUE } from '@/pages/dashboard/_config/constant';
import CascadeFilterComponent from '@/pages/dashboard/components/CascadeFilterComponent';
import type { ICascadeFilterValue, TCascadeFilterOption } from '@/pages/dashboard/components/CascadeFilterComponent/_type';
import type { IAiPromptOption } from '../types';
import {
  AI_SCOPE_CHILD_KEY,
  AI_SCOPE_FILTER_KEY,
  AI_SCOPE_PARENT_KEY,
  convertCascadeValueToScope,
} from './scope';

export interface ISettingsProps {
  onPreview: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** 为 true：主表单灰置且确定按钮 loading（仅「确定」提交流程） */
  submitting: boolean;
  /** 为 true：「下次自动应用」按钮 loading，不灰置表单项 */
  autoApplying?: boolean;
  /** 报表编辑模式下展示「设置模板」入口 */
  isEdit?: boolean;
  onAutoApplyNextTime?: () => void;
  onOpenSaveTemplate?: () => void;
  locale: string;
  scopeOptions: TCascadeFilterOption[];
  allTaskIds: string[];
  scopeValue: ICascadeFilterValue;
  scopeErrorText?: string;
  onScopeChange: (value: { type: 'all' | 'chart'; taskIds: string[]; }) => void;
  options: IAiPromptOption[];
  selectedApproachId: number | string;
  promptContent: string;
  analysisContext: string;
  onAnalysisContextChange: (value: string) => void;
  onApproachSelectChange: (
    value: number | string,
    context: { event: unknown; option?: SelectOption; },
  ) => void;
  onPromptContentChange: (value: string) => void;
}

const MAX_TEXT_LENGTH = 50000;

const Settings: React.FC<ISettingsProps> = (props) => {
  const {
    onPreview,
    onConfirm,
    onCancel,
    submitting,
    autoApplying = false,
    isEdit = false,
    onAutoApplyNextTime,
    onOpenSaveTemplate,
    locale,
    scopeOptions,
    allTaskIds,
    scopeValue,
    scopeErrorText,
    onScopeChange,
    options,
    selectedApproachId,
    promptContent,
    analysisContext,
    onAnalysisContextChange,
    onApproachSelectChange,
    onPromptContentChange,
  } = props;
  const { formatMessage } = useIntl();

  const isPromptContentValid = Boolean(promptContent.trim());
  const actionDisabled = submitting || !isPromptContentValid;

  const approachSelectOptions = useMemo<SelectOption[]>(
    () => options.map((opt) => ({
      ...opt,
      value: String(opt.value),
    })),
    [options],
  );
  const approachSelectValue = String(selectedApproachId);

  const handlePreview = useCallback(() => {
    if (actionDisabled) {
      return;
    }

    onPreview();
  }, [actionDisabled, onPreview]);

  const handleConfirm = useCallback(() => {
    if (actionDisabled) {
      return;
    }

    onConfirm();
  }, [actionDisabled, onConfirm]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  /** 下次自动应用：请求进行中仅防连点，不联动 actionDisabled 灰置其它控件 */
  const handleAutoApplyNextTime = useCallback(() => {
    if (!isPromptContentValid || autoApplying || submitting) {
      return;
    }
    onAutoApplyNextTime?.();
  }, [autoApplying, isPromptContentValid, onAutoApplyNextTime, submitting]);

  /** 设置模板：仅编辑态展示，打开二级弹窗由上层控制 */
  const handleOpenSaveTemplate = useCallback(() => {
    if (actionDisabled) {
      return;
    }
    onOpenSaveTemplate?.();
  }, [actionDisabled, onOpenSaveTemplate]);

  const handleScopeChange = useCallback((_key: string, value: ICascadeFilterValue) => {
    onScopeChange(convertCascadeValueToScope(value, allTaskIds));
  }, [allTaskIds, onScopeChange]);

  return (
    <div className="dbl-ai-analysis-settings-form">
      <div className="dbl-ai-analysis-settings-form__content-wrap">
        <DatalabGrid
          colCount={2}
          gap={20}
          rowGap={16}
          columnWidthMode={['max-content', '1fr']}
          verticalAlign="start"
          align="stretch"
        >
          <div className="dbl-ai-analysis-settings-form__label">
            {formatMessage({ id: 'dbl_ai_analysis_settings_scope_label' })}
          </div>
          <div className="dbl-ai-analysis-settings-form__content dbl-ai-analysis-settings-scope">
            <CascadeFilterComponent
              className="dbl-ai-analysis-settings-scope__selector"
              cascadeBoxClassName="dbl-ai-analysis-settings-scope__selector-box"
              filterKey={AI_SCOPE_FILTER_KEY}
              value={scopeValue}
              options={scopeOptions}
              onChange={handleScopeChange}
              locale={locale}
              name=""
              lftName={formatMessage({ id: 'dbl_ai_analysis_settings_scope_tabs_label' })}
              parentKey={AI_SCOPE_PARENT_KEY}
              childKey={AI_SCOPE_CHILD_KEY}
              radioOptions={[
                {
                  value: ALL_VALUE,
                  text: 'Entire Report',
                  textCN: '整个报表',
                },
              ]}
            />
            {scopeErrorText ? (
              <div className="dbl-ai-analysis-settings-scope__error">
                {scopeErrorText}
              </div>
            ) : null}
          </div>

          <div className="dbl-ai-analysis-settings-form__label">
            {formatMessage({ id: 'dbl_ai_analysis_settings_approach_label' })}
          </div>
          <div className={classNames('dbl-ai-analysis-settings-form__content dbl-ai-analysis-settings-approach', {
            'dbl-ai-analysis-settings-approach--error': !isPromptContentValid,
          })}>
            <Select
              className="dbl-ai-analysis-settings-approach__select"
              matchButtonWidth
              size="m"
              appearance="button"
              disabled={submitting}
              options={approachSelectOptions}
              value={approachSelectValue}
              onChange={onApproachSelectChange}
            />

            <div className="dbl-ai-analysis-settings-approach__hint">
              {formatMessage({ id: 'dbl_ai_analysis_settings_custom_hint' })}
            </div>

            <Input.TextArea
              className="dbl-ai-analysis-settings-textarea"
              value={promptContent}
              disabled={submitting}
              onChange={onPromptContentChange}
              maxLength={MAX_TEXT_LENGTH}
              showCount
              placeholder={formatMessage({ id: 'dbl_ai_analysis_settings_approach_placeholder' })}
            />
          </div>

          <div className="dbl-ai-analysis-settings-form__label dbl-ai-analysis-settings-form__label--background">
            {formatMessage({ id: 'dbl_ai_analysis_settings_background_label' })}
            <div>
              {formatMessage({ id: 'dbl_ai_analysis_settings_optional' })}
            </div>
          </div>
          <div className="dbl-ai-analysis-settings-form__content">
            <Input.TextArea
              className="dbl-ai-analysis-settings-textarea"
              value={analysisContext}
              disabled={submitting}
              onChange={onAnalysisContextChange}
              maxLength={MAX_TEXT_LENGTH}
              showCount
              placeholder={formatMessage({ id: 'dbl_ai_analysis_settings_background_placeholder' })}
            />
          </div>
        </DatalabGrid>
      </div>

      {/* 按钮顺序：预览 → 下次自动应用 → 设置模板（仅编辑态）→ 确定 → 取消 */}
      <div className="dbl-ai-analysis-settings__actions">
        <Button onClick={handlePreview} disabled={actionDisabled}>
          {formatMessage({ id: 'dbl_ai_analysis_settings_action_preview' })}
        </Button>
        {
          !isEdit ? (
            <Button
              onClick={handleAutoApplyNextTime}
              loading={autoApplying}
              disabled={!isPromptContentValid}
            >
              {formatMessage({ id: 'dbl_ai_analysis_settings_action_auto_apply_next_time' })}
            </Button>
          )
            : null
        }
        {isEdit ? (
          <Button onClick={handleOpenSaveTemplate} disabled={actionDisabled}>
            {formatMessage({ id: 'dbl_ai_analysis_settings_action_save_template' })}
          </Button>
        ) : null}
        <Button type="primary" onClick={handleConfirm} loading={submitting} disabled={actionDisabled}>
          {formatMessage({ id: 'dbl_ai_analysis_settings_action_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel} disabled={submitting}>
          {formatMessage({ id: 'dbl_ai_analysis_settings_action_cancel' })}
        </Button>
      </div>
    </div>
  );
};

export default memo(Settings);
